import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  GraduationCap, Users, Smartphone, CreditCard, Receipt, BarChart3, Mail, Lock, Eye, EyeOff,
} from "lucide-react";
import { useAuth, homeForRole } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const DEFAULT_COVER = "https://images.unsplash.com/photo-1745501231916-9a723ba690da?crop=entropy&cs=srgb&fm=jpg&q=85";
const CERT_BADGE = "https://customer-assets-7cd3h4nn.emergentagent.net/job_edu-manage-100/artifacts/43b8z0rh_certificado%201.png";
const CERT_URL = "https://net-blue.com/";

const FEATURES = [
  { icon: GraduationCap, label: "Gestión Académica", color: "bg-indigo-100 text-indigo-700" },
  { icon: Users, label: "Padres y Familias", color: "bg-emerald-100 text-emerald-700" },
  { icon: Smartphone, label: "App Móvil", color: "bg-sky-100 text-sky-700" },
  { icon: CreditCard, label: "Pagos en Línea", color: "bg-blue-100 text-blue-700" },
  { icon: Receipt, label: "Facturación DGII", color: "bg-teal-100 text-teal-700" },
  { icon: BarChart3, label: "Reportes Avanzados", color: "bg-violet-100 text-violet-700" },
];

export default function Login() {
  const { login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [brand, setBrand] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/platform/settings`)
      .then((r) => r.json())
      .then(setBrand)
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      navigate(homeForRole(user));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const brandName = brand?.company_name || "EduGestión RD";
  const tagline = brand?.tagline || "Todo tu colegio. Una sola plataforma.";
  const slogan = brand?.slogan || "La educación nos une";

  const logoBlock = (size) => {
    if (brand?.logo_url && brand?.logo_has_name) {
      return (
        <img src={brand.logo_url} alt={brandName} className="w-auto object-contain" style={{ height: size * 1.35 }} data-testid="login-logo" />
      );
    }
    return (
      <div className="flex items-center gap-3">
        {brand?.logo_url && (
          <img src={brand.logo_url} alt={brandName} className="w-auto object-contain" style={{ height: size }} data-testid="login-logo" />
        )}
        <div>
          <p className="font-extrabold leading-tight text-slate-900"
             style={{ fontFamily: "Outfit, sans-serif", fontSize: size * 0.55 }}>
            {brandName}
          </p>
          <p className="text-slate-600 leading-tight" style={{ fontSize: Math.max(11, size * 0.18) }}>{tagline}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex bg-white">
      {/* Panel izquierdo — portada */}
      <div className="hidden lg:block lg:w-[68%] relative overflow-hidden bg-white" data-testid="login-cover">
        <img
          src={brand?.cover_url || DEFAULT_COVER}
          alt=""
          className="absolute inset-0 w-full h-full"
          style={{
            objectFit: brand?.cover_has_design ? "contain" : "cover",
            objectPosition: brand?.cover_position || "center",
            transform: `scale(${(brand?.cover_zoom ?? 100) / 100})`,
          }}
        />
        {!brand?.cover_has_design && (
          <>
            {/* Logo + tagline arriba a la izquierda */}
            <div className="absolute top-10 left-10 drop-shadow-lg" data-testid="cover-brand">
              {logoBlock(72)}
            </div>
            {/* Insignias de características */}
            <div className="absolute left-10 top-1/3 grid grid-cols-3 gap-x-6 gap-y-8 max-w-md" data-testid="cover-features">
              {FEATURES.map((f, i) => (
                <div key={f.label} className="flex flex-col items-center gap-2 text-center">
                  <div className={`w-14 h-14 rounded-full ${f.color} bg-opacity-90 backdrop-blur-sm flex items-center justify-center shadow-lg`}>
                    <f.icon className="w-6 h-6" />
                  </div>
                  <p className="text-xs font-semibold text-slate-800 drop-shadow-[0_1px_2px_rgba(255,255,255,0.9)] leading-tight">{(t("login.features") || [])[i] || f.label}</p>
                </div>
              ))}
            </div>
            {/* Onda inferior con lema */}
            <div className="absolute bottom-0 left-0 right-0">
              <svg viewBox="0 0 600 140" preserveAspectRatio="none" className="w-full h-36 block">
                <defs>
                  <linearGradient id="wave" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#1d4ed8" />
                    <stop offset="60%" stopColor="#2563eb" />
                    <stop offset="100%" stopColor="#10b981" />
                  </linearGradient>
                </defs>
                <path d="M0,60 C120,10 240,20 340,50 C440,80 540,70 600,40 L600,140 L0,140 Z" fill="url(#wave)" opacity="0.95" />
              </svg>
              <p
                className="absolute bottom-12 left-10 text-white text-4xl leading-tight"
                style={{ fontFamily: "'Dancing Script', cursive" }}
                data-testid="cover-slogan"
              >
                {slogan}
                <svg viewBox="0 0 200 12" className="w-48 h-3 mt-1">
                  <path d="M4,8 C60,2 140,2 196,7" stroke="#10b981" strokeWidth="5" fill="none" strokeLinecap="round" />
                </svg>
              </p>
            </div>
          </>
        )}
      </div>

      {/* Panel derecho — formulario */}
      <div className="flex-1 relative flex items-center justify-center p-6">
        <div className="absolute top-5 right-5 z-10">
          <LanguageSwitcher testid="language-switcher-login" />
        </div>
        {/* Certificado — abajo a la derecha del formulario, enlace externo */}
        <a
          href={CERT_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="cover-certificate-link"
          title="Network 065 Dominicana — Certificado Seguro"
          className="absolute bottom-6 right-6 z-10 transition-transform hover:scale-105"
        >
          <img
            src={CERT_BADGE}
            alt="Network 065 Dominicana — Certificado Seguro"
            className="h-20 w-auto drop-shadow-xl"
            data-testid="cover-certificate"
          />
        </a>
        <form onSubmit={submit} className="w-full max-w-md space-y-5" data-testid="login-form">
          <div className="flex justify-center mb-1">{logoBlock(56)}</div>
          <h1 className="text-xl font-bold text-slate-900 text-center" data-testid="login-heading">
            {t("login.title")}
          </h1>
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-slate-800 font-medium">{t("login.email")}</Label>
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-blue-600" />
              <Input
                id="email" type="email" placeholder="tu@correo.com"
                data-testid="login-email-input"
                className="pl-10 h-11 rounded-lg border-slate-300"
                value={email} onChange={(e) => setEmail(e.target.value)} required
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-slate-800 font-medium">{t("login.password")}</Label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-blue-600" />
              <Input
                id="password" type={showPassword ? "text" : "password"} placeholder="Ingresa tu contraseña"
                data-testid="login-password-input"
                className="pl-10 pr-10 h-11 rounded-lg border-slate-300"
                value={password} onChange={(e) => setPassword(e.target.value)} required
              />
              <button
                type="button"
                data-testid="toggle-password-visibility"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <Button
            type="submit"
            data-testid="login-submit-button"
            disabled={loading}
            className="w-full h-11 rounded-lg bg-blue-600 hover:bg-blue-700 text-base font-semibold"
          >
            {loading ? t("login.loading") : t("login.submit")}
          </Button>
          <div className="text-xs text-slate-400 border-t border-slate-200 pt-4 space-y-1 text-center">
            <p className="font-semibold text-slate-600">Cuentas demo (Colegio Demo ABC):</p>
            <p>admin@demoabc.edu.do · secretaria@demoabc.edu.do ·</p>
            <p>maestra@demoabc.edu.do · padre@demoabc.edu.do</p>
            <p>Contraseña: Demo123!</p>
          </div>
        </form>
      </div>
    </div>
  );
}
